import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import Modal from "@/components/ui/modal";
import Input from "@/components/ui/input";
import Textarea from "@/components/ui/textarea";
import Dropdown from "@/components/ui/dropdown";
import type {
  Department,
  CreateDepartmentPayload,
} from "../types/departments.types";
import styles from "./DepartmentModal.module.scss";

// Merkez/Taşra ayrımı kaldırıldı: birim tipi seçimi yok, tüm birimler Merkez.
const departmentFormSchema = z.object({
  name: z
    .string()
    .min(2, "departmentModal.validation.nameMin")
    .max(100, "departmentModal.validation.nameMax"),
  description: z.string().max(500).optional(),
  location: z.string().max(500).optional(),
  parentID: z.string().optional(),
  supervisorID: z.string().optional(),
});

export interface DepartmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateDepartmentPayload) => void;
  departments: Department[];
  supervisors: { id: string; name: string }[];
  onSearchSupervisors?: (search: string) => void;
  locations?: { id: string; name: string; address?: string }[];
  onSearchLocations?: (search: string) => void;
  editDepartment?: Department | null;
  title?: string;
}

const DepartmentModal: React.FC<DepartmentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  departments,
  supervisors,
  onSearchSupervisors,
  locations = [],
  onSearchLocations,
  editDepartment = null,
  title,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [parentID, setParentID] = useState("");
  const [supervisorID, setSupervisorID] = useState("");
  const [isFormValid, setIsFormValid] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const parentOptions = useMemo(() => {
    const eligible = departments.filter((d) => d.id !== editDepartment?.id);
    return [
      {
        label: t("departments.noParent"),
        value: "",
      },
      ...eligible.map((d) => ({ label: d.name, value: d.id })),
    ];
  }, [departments, editDepartment?.id, t]);

  const supervisorOptions = useMemo(
    () => [
      { label: t("departments.noSupervisor"), value: "" },
      ...supervisors.map((s) => ({ label: s.name, value: s.id })),
    ],
    [supervisors, t],
  );

  const locationOptions = useMemo(() => {
    const base = [
      { label: t("departments.noLocation"), value: "" },
      ...locations.map((l) => ({
        label: l.address ? `${l.name} — ${l.address}` : l.name,
        value: l.name,
      })),
    ];
    // Ensure current value is in the list (in case backend has an older free-text value not matching any location)
    if (location && !base.some((o) => o.value === location)) {
      base.push({ label: location, value: location });
    }
    return base;
  }, [locations, location, t]);

  const selectedParentLabel =
    parentID === ""
      ? (parentOptions[0]?.label ?? "")
      : (parentOptions.find((o) => o.value === parentID)?.label ?? "");

  const selectedSupervisorLabel =
    supervisorID === ""
      ? (supervisorOptions[0]?.label ?? "")
      : (supervisorOptions.find((o) => o.value === supervisorID)?.label ?? "");

  const selectedLocationLabel =
    location === ""
      ? (locationOptions[0]?.label ?? "")
      : (locationOptions.find((o) => o.value === location)?.label ?? location);

  useEffect(() => {
    const result = departmentFormSchema.safeParse({
      name: name.trim(),
      description: description.trim() || undefined,
      location: location.trim() || undefined,
      parentID: parentID || undefined,
      supervisorID: supervisorID || undefined,
    });
    setIsFormValid(result.success);
  }, [name, description, location, parentID, supervisorID]);

  useEffect(() => {
    if (isOpen) {
      setFieldErrors({});
      if (editDepartment) {
        setName(editDepartment.name ?? "");
        setDescription(editDepartment.description ?? "");
        setLocation(editDepartment.location ?? "");
        setParentID(editDepartment.parentID ?? "");
        setSupervisorID(editDepartment.supervisorID ?? "");
      } else {
        setName("");
        setDescription("");
        setLocation("");
        setParentID("");
        setSupervisorID("");
      }
    }
  }, [isOpen, editDepartment]);

  const validateForm = () => {
    const result = departmentFormSchema.safeParse({
      name: name.trim(),
      description: description.trim() || undefined,
      location: location.trim() || undefined,
      parentID: parentID || undefined,
      supervisorID: supervisorID || undefined,
    });
    if (result.success) {
      setFieldErrors({});
      return true;
    }
    const flattened = result.error.flatten().fieldErrors;
    const errors: Record<string, string> = {};
    if (flattened.name?.[0]) errors.name = flattened.name[0];
    setFieldErrors(errors);
    return false;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) return;
    const payload: CreateDepartmentPayload = {
      name: name.trim(),
      description: description.trim() || undefined,
      location: location.trim() || undefined,
      parentID: parentID || null,
      supervisorID: supervisorID || null,
    };
    onSubmit(payload);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title ?? t("departments.addTitle")}
      size="lg"
      withForm
      formId="departmentForm"
      handleSubmit={handleSubmit}
      isFormValid={isFormValid}
      submitButtonText={t("buttons.save")}
    >
      <div className={styles.form}>
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionNumber}>1</span>
            <div>
              <h4 className={styles.sectionTitle}>
                {t("departments.sectionBasic")}
              </h4>
              <p className={styles.sectionSubtitle}>
                {t("departments.sectionBasicHint")}
              </p>
            </div>
          </div>
          <div className={styles.grid2}>
            <Input
              label={t("departments.name")}
              placeholder={t("departments.namePlaceholder")}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (fieldErrors.name)
                  setFieldErrors((p) => ({ ...p, name: "" }));
              }}
              error={!!fieldErrors.name}
              errorMessage={fieldErrors.name}
              maxLength={100}
              required
            />
            <Dropdown
              label={t("departments.parent")}
              placeholder={t("departments.selectParent")}
              options={parentOptions}
              value={parentID}
              selectedLabel={selectedParentLabel}
              onSelect={(v) =>
                setParentID(Array.isArray(v) ? (v[0] ?? "") : v)
              }
              allowClear
              searchable
            />
          </div>
          {parentOptions.length === 1 && (
            <p className={styles.inlineHint}>
              {t("departments.noParentAvailable")}
            </p>
          )}
        </section>

        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionNumber}>2</span>
            <div>
              <h4 className={styles.sectionTitle}>
                {t("departments.sectionDetails")}
              </h4>
              <p className={styles.sectionSubtitle}>
                {t("departments.sectionDetailsHint")}
              </p>
            </div>
          </div>
          <div className={styles.grid2}>
            <Dropdown
              label={t("departments.supervisor")}
              placeholder={t("departments.selectSupervisor")}
              options={supervisorOptions}
              value={supervisorID}
              selectedLabel={selectedSupervisorLabel}
              onSelect={(v) =>
                setSupervisorID(Array.isArray(v) ? (v[0] ?? "") : v)
              }
              onSearch={onSearchSupervisors}
              searchable
              allowClear
            />
            <Dropdown
              label={t("departments.location")}
              placeholder={t("departments.selectLocation")}
              options={locationOptions}
              value={location}
              selectedLabel={selectedLocationLabel}
              onSelect={(v) =>
                setLocation(Array.isArray(v) ? (v[0] ?? "") : v)
              }
              onSearch={onSearchLocations}
              searchable
              allowClear
            />
          </div>
          <Textarea
            label={t("departments.description")}
            placeholder={t("departments.descriptionPlaceholder")}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={500}
            rows={3}
          />
        </section>
      </div>
    </Modal>
  );
};

export default DepartmentModal;
